import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Flame } from "lucide-react";
import { URGENCY } from "../data/content";

export const UrgencyPopup = ({ onBookVisit }) => {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (shown) return;
      const scrolled =
        window.scrollY /
        (document.documentElement.scrollHeight - window.innerHeight);
      if (scrolled >= 0.2) {
        setOpen(true);
        setShown(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [shown]);

  const close = () => setOpen(false);

  const handleBook = () => {
    close();
    onBookVisit();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="urgency-popup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 z-[60] grid place-items-center bg-[#142415]/70 backdrop-blur-sm px-4"
        >
          <motion.div
            data-testid="urgency-popup-card"
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl bg-[#F7F3EE] shadow-2xl overflow-hidden"
          >
            <button
              data-testid="urgency-popup-close"
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-black/10 grid place-items-center text-[#1F291E] transition-colors hover:bg-black/20"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="bg-[#2C5F2D] px-7 pt-8 pb-7 text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#C15B3D] px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-white">
                <Flame className="h-3.5 w-3.5" />
                {URGENCY.popup.badge}
              </span>
              <h3 className="mt-5 font-serif text-2xl sm:text-3xl text-white leading-snug">
                {URGENCY.popup.title}
              </h3>
            </div>

            <div className="px-7 py-7">
              <ul className="space-y-4">
                {URGENCY.popup.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#2C5F2D]/10 grid place-items-center">
                      <Check className="h-4 w-4 text-[#2C5F2D]" />
                    </span>
                    <span className="text-[15px] text-[#1F291E] leading-relaxed">
                      {p}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                data-testid="urgency-popup-cta"
                onClick={handleBook}
                className="mt-7 w-full rounded-full bg-[#C15B3D] px-6 py-4 text-base font-semibold text-white shadow-lg shadow-[#C15B3D]/25 transition-all hover:bg-[#A84C31] hover:-translate-y-0.5"
              >
                {URGENCY.popup.cta}
              </button>
              <button
                onClick={close}
                className="mt-3 w-full text-center text-sm text-[#5C6B5A] hover:text-[#1F291E] transition-colors"
              >
                Maybe later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
